import { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';

interface UseHlsPlayerOptions {
  /** 是否启用 HLS 播放 */
  enabled: boolean;
  /** HLS master.m3u8 URL */
  hlsUrl: string | undefined;
  /** 获取认证头的函数 */
  getAuthHeaders: () => Record<string, string>;
  /** 致命错误回调 */
  onFatalError?: (error: Error) => void;
  /** 一般错误回调（非致命） */
  onError?: (error: Error, details: string) => void;
}

/**
 * 检测是否为 iOS Safari
 */
function isIOSSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|OPiOS/.test(ua);
  return isIOS && isSafari;
}

/**
 * HLS 播放器 Hook
 * 
 * 功能：
 * - iOS Safari 使用原生 HLS + Cookie 认证（性能最优）
 * - 其他浏览器使用 hls.js + xhrSetup 认证
 * - 自动错误恢复（网络错误、媒体错误）
 * - 致命错误时清理资源并回调
 * 
 * 关键设计：
 * 1. 使用 videoEl state 解决 ref 就绪不触发 effect 的问题
 * 2. 使用 useCallback/ref 缓存函数，避免每次 render 重建 Hls 实例
 * 3. iOS Safari 使用原生 HLS + Cookie（后端已支持 Cookie 认证）
 * 4. Hook 不修改外部 ref，仅返回 callback ref，由组件层 mergeRefs
 */
export function useHlsPlayer({
  enabled,
  hlsUrl,
  getAuthHeaders,
  onFatalError,
  onError,
}: UseHlsPlayerOptions) {
  const hlsRef = useRef<Hls | null>(null);
  // 使用 state 存储 video 元素，解决 ref 就绪不触发 effect 的问题
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  
  // 使用 ref 缓存回调函数，避免每次 render 重建
  const getAuthHeadersRef = useRef(getAuthHeaders);
  const onFatalErrorRef = useRef(onFatalError);
  const onErrorRef = useRef(onError);

  // 更新 ref 缓存
  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders;
    onFatalErrorRef.current = onFatalError;
    onErrorRef.current = onError;
  }, [getAuthHeaders, onFatalError, onError]);

  // 使用 callback ref 监听 video 元素就绪（不修改外部 ref）
  const videoCallbackRef = useCallback((element: HTMLVideoElement | null) => {
    setVideoEl(element);
  }, []);

  // 检测是否为 iOS Safari
  const useNativeHls = isIOSSafari();

  useEffect(() => {
    // 如果未启用或没有 URL，清理并退出
    if (!enabled || !hlsUrl) {
      // 清理 hls.js 实例
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // 清理原生 HLS（更彻底的清理）
      if (videoEl) {
        videoEl.pause();
        videoEl.removeAttribute('src');
        videoEl.load();
      }
      return;
    }

    // 等待 video 元素就绪
    if (!videoEl) {
      return;
    }

    // iOS Safari：使用原生 HLS + Cookie 认证
    if (useNativeHls) {
      // 进入原生 HLS 分支前，必须清理 hls.js 实例（如果存在）
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      // 确保 video.src 设置为 HLS URL（原生 HLS 需要）
      if (videoEl.src !== hlsUrl) {
        videoEl.src = hlsUrl;
      }
      console.log('✓ iOS Safari 使用原生 HLS 播放（Cookie 认证）:', hlsUrl);
      return;
    }

    // 其他浏览器：使用 hls.js
    // 检查 hls.js 是否支持
    if (!Hls.isSupported()) {
      const error = new Error('浏览器不支持 HLS 播放（hls.js 不支持）');
      console.error(error);
      if (onFatalErrorRef.current) {
        onFatalErrorRef.current(error);
      }
      return;
    }

    // 清理旧的 HLS 实例
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // 确保 video.src 为空（hls.js 模式下必须）
    if (videoEl.src) {
      videoEl.src = '';
      videoEl.removeAttribute('src');
    }

    // 创建 hls.js 实例（使用缓存的函数）
    const hls = new Hls({
      // 配置认证头（使用缓存的函数）
      xhrSetup: (xhr: XMLHttpRequest, url: string) => {
        const headers = getAuthHeadersRef.current();
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      },
      // 性能优化配置
      enableWorker: true,
      lowLatencyMode: false,
      // 初始缓冲优化：减少初始缓冲时间，更快开始播放
      maxBufferLength: 30,        // 最大缓冲长度（秒），默认 30
      maxMaxBufferLength: 60,      // 最大缓冲上限（秒），默认 600，降低以减少内存占用
      startLevel: -1,             // 自动选择最佳码率（-1 表示自动）
      // 预加载优化：提前加载更多分片，减少卡顿
      maxBufferSize: 60 * 1000 * 1000,  // 最大缓冲大小（60MB），默认 60MB
      maxBufferHole: 0.5,         // 允许的最大缓冲空洞（秒），默认 0.5
      // 调试（开发环境）
      debug: process.env.NODE_ENV === 'development',
    });

    // 先 attachMedia，然后在 MEDIA_ATTACHED 事件里 loadSource（更安全的初始化顺序）
    hls.attachMedia(videoEl);

    // 监听 MEDIA_ATTACHED 事件，然后加载源
    hls.once(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(hlsUrl);
      console.log('✓ HLS 播放器已初始化（hls.js）:', hlsUrl);
    });

    // 错误处理（使用缓存的回调）
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.error('HLS 网络错误，尝试恢复...', data);
            try {
              hls.startLoad();
              if (onErrorRef.current) {
                onErrorRef.current(new Error('HLS 网络错误'), 'NETWORK_ERROR');
              }
            } catch (e) {
              console.error('HLS 网络错误恢复失败:', e);
              hls.destroy();
              hlsRef.current = null;
              if (onFatalErrorRef.current) {
                onFatalErrorRef.current(new Error(`HLS 网络错误恢复失败: ${data.details}`));
              }
            }
            break;

          case Hls.ErrorTypes.MEDIA_ERROR:
            console.error('HLS 媒体错误，尝试恢复...', data);
            try {
              hls.recoverMediaError();
              if (onErrorRef.current) {
                onErrorRef.current(new Error('HLS 媒体错误'), 'MEDIA_ERROR');
              }
            } catch (e) {
              console.error('HLS 媒体错误恢复失败:', e);
              hls.destroy();
              hlsRef.current = null;
              if (onFatalErrorRef.current) {
                onFatalErrorRef.current(new Error(`HLS 媒体错误恢复失败: ${data.details}`));
              }
            }
            break;

          default:
            // 其他致命错误
            console.error('HLS 致命错误:', data);
            hls.destroy();
            hlsRef.current = null;
            if (onFatalErrorRef.current) {
              onFatalErrorRef.current(new Error(`HLS 致命错误: ${data.type} - ${data.details}`));
            }
            break;
        }
      } else {
        // 非致命错误，仅记录
        console.warn('HLS 非致命错误:', data);
      }
    });

    // 保存实例
    hlsRef.current = hls;

    // 清理函数
    return () => {
      if (hlsRef.current) {
        console.log('清理 HLS 播放器实例');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // 清理原生 HLS（如果切换模式）
      if (videoEl && useNativeHls) {
        videoEl.pause();
        videoEl.removeAttribute('src');
        videoEl.load();
      }
    };
  }, [enabled, hlsUrl, videoEl, useNativeHls]);

  // 返回 callback ref 和 hls 实例 ref
  return {
    videoCallbackRef,
    hlsRef,
  };
}
